import { useState, lazy, Suspense } from "react"
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom"
import Header from "./components/layout/Header"
import Footer from "./components/layout/Footer"
import { Toaster } from "react-hot-toast"
import { ConfigProvider } from "./contexts/ConfigContext"
import { useAuth } from "./contexts/auth-context"

// Lazy Loading de Vistas
const HomeView = lazy(() => import("./views/HomeView"))
const LoginView = lazy(() => import("./views/LoginView"))
const ForgotPasswordView = lazy(() => import("./views/ForgotPasswordView"))
const CatalogoView = lazy(() => import("./views/CatalogoView"))
const BlogView = lazy(() => import("./views/BlogView"))
const OrdenesView = lazy(() => import("./views/OrdenesView"))
const AdminView = lazy(() => import("./views/AdminView"))
const SolicitudEspecial = lazy(() => import("./views/SolicitudesView"))
const DetalleProducto = lazy(() => import("./views/DetalleProducto"))
const ArticuloDetalle = lazy(() => import("./views/ArticuloDetalle"))
const PageNotFound = lazy(() => import("./views/PageNotFound"))
const PrivacyPolicy = lazy(() => import("./views/PrivacyPolicyView"))
const TermsConditions = lazy(() => import("./views/TermsConditionsView"))

// Componente para proteger rutas
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Verificando autenticación...</p>
        </div>
      </div>
    )
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />
}
// Componente de Layout para manejar renderizado condicional de Header/Footer
function MainLayout({
  children,
  carrito,
  totalItems,
  mostrarCarrito,
  setMostrarCarrito,
  eliminarDelCarrito,
  vaciarCarrito,
  agregarAlCarrito,
  reducirCantidad
}) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Rutas que no deben tener Header/Footer por defecto (como login si se quisiera, pero por ahora solo admin)
  const isCleanLayout = isAdminRoute;

  return (
    <>
      <Toaster position="top-center" />

      {!isCleanLayout && (
        <Header
          carrito={carrito}
          totalItems={totalItems}
          mostrarCarrito={mostrarCarrito}
          setMostrarCarrito={setMostrarCarrito}
          eliminarDelCarrito={eliminarDelCarrito}
          vaciarCarrito={vaciarCarrito}
        />
      )}

      {children}

      {!isCleanLayout && <Footer />}
    </>
  );
}

function App() {
  const [carrito, setCarrito] = useState([])
  const [mostrarCarrito, setMostrarCarrito] = useState(false)

  const { isAuthenticated, loading } = useAuth()

  // Función para agregar producto al carrito
  const agregarAlCarrito = (producto) => {
    setCarrito((prev) => {
      const index = prev.findIndex((p) => p.id === producto.id)
      if (index === -1) {
        return [...prev, { ...producto, cantidad: 1 }]
      } else {
        return prev.map((p, i) => (i === index ? { ...p, cantidad: p.cantidad + 1 } : p))
      }
    })
  }

  const eliminarDelCarrito = (productoId) => {
    setCarrito((prev) => prev.filter((p) => p.id !== productoId))
  }

  const totalItems = carrito.reduce((acc, item) => acc + item.cantidad, 0)

  const reducirCantidad = (productoId) => {
    setCarrito((prev) => {
      return prev.reduce((acc, producto) => {
        if (producto.id === productoId) {
          if (producto.cantidad > 1) {
            acc.push({ ...producto, cantidad: producto.cantidad - 1 })
          }
        } else {
          acc.push(producto)
        }
        return acc
      }, [])
    })
  }

  // Si está cargando, mostrar indicador global
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Cargando aplicación...</p>
        </div>
      </div>
    )
  }

  return (
    <ConfigProvider>
      <Router>
        <MainLayout
          carrito={carrito}
          totalItems={totalItems}
          mostrarCarrito={mostrarCarrito}
          setMostrarCarrito={setMostrarCarrito}
          eliminarDelCarrito={eliminarDelCarrito}
          vaciarCarrito={() => setCarrito([])}
          agregarAlCarrito={agregarAlCarrito}
          reducirCantidad={reducirCantidad}
        >
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-yellow-500 mx-auto mb-4"></div>
                  <p className="text-gray-600 font-medium">Cargando...</p>
                </div>
              </div>
            }
          >
            <Routes>
              {/* Rutas públicas */}
              <Route path="/" element={<HomeView />} />
              <Route path="/catalogo" element={<CatalogoView agregarAlCarrito={agregarAlCarrito} carrito={carrito} />} />
              <Route
                path="/producto/:id"
                element={<DetalleProducto agregarAlCarrito={agregarAlCarrito} carrito={carrito} />}
              />
              <Route path="/blog" element={<BlogView />} />
              <Route path="/articulo/:id" element={<ArticuloDetalle />} />
              <Route path="/solicitudes" element={<SolicitudEspecial />} />
              <Route path="/politica-privacidad" element={<PrivacyPolicy />} />
              <Route path="/terminos-condiciones" element={<TermsConditions />} />
              <Route
                path="/ordenes"
                element={
                  <OrdenesView
                    carrito={carrito}
                    eliminarDelCarrito={eliminarDelCarrito}
                    reducirCantidad={reducirCantidad}
                    agregarAlCarrito={agregarAlCarrito}
                    vaciarCarrito={() => setCarrito([])}
                  />
                }
              />

              {/* Rutas de autenticación */}
              <Route path="/login" element={isAuthenticated ? <Navigate to="/admin" replace /> : <LoginView />} />
              <Route path="/forgot-password" element={<ForgotPasswordView />} />

              {/* Rutas protegidas del admin - TODAS LAS RUTAS ESPECÍFICAS */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/inventario"
                element={
                  <ProtectedRoute>
                    <AdminView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reportes"
                element={
                  <ProtectedRoute>
                    <AdminView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/clientes"
                element={
                  <ProtectedRoute>
                    <AdminView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/usuarios"
                element={
                  <ProtectedRoute>
                    <AdminView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/roles"
                element={
                  <ProtectedRoute>
                    <AdminView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/ventas"
                element={
                  <ProtectedRoute>
                    <AdminView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/blog"
                element={
                  <ProtectedRoute>
                    <AdminView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/marcas"
                element={
                  <ProtectedRoute>
                    <AdminView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/pendientes"
                element={
                  <ProtectedRoute>
                    <AdminView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/configuracion"
                element={
                  <ProtectedRoute>
                    <AdminView />
                  </ProtectedRoute>
                }
              />

              {/* Rutas legacy para compatibilidad */}
              <Route path="/inventario" element={<Navigate to="/admin/inventario" replace />} />
              <Route path="/reporte" element={<Navigate to="/admin/reportes" replace />} />
              <Route path="/crear-usuario" element={<Navigate to="/admin/usuarios" replace />} />
              <Route path="/usuarios" element={<Navigate to="/admin/usuarios" replace />} />

              {/* Ruta 404 */}
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </Suspense>
        </MainLayout>
      </Router>
    </ConfigProvider>
  )
}

export default App
